import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { FaWhatsapp } from "react-icons/fa";
import WhatsappTemplatePreviewModal from "@/app/components/ui/WhatsappTemplatePreviewModal";
import {
  ISmartQCDeeplinkCard,
  useSmartQCDeeplinkCardsAPI,
} from "@/app/hooks/api_hooks/smartQCDeeplink/useSmartQCDeeplinkCardsAPI";
import { useSmartQCDeeplinkScriptAPI } from "@/app/hooks/api_hooks/smartQCDeeplink/useSmartQCDeeplinkScriptAPI";
import { useSendSmartQCDeeplinkWhatsappAPI } from "@/app/hooks/api_hooks/smartQCDeeplink/useSendSmartQCDeeplinkWhatsappAPI";

export interface ISmartQCDeeplinkModalProps {
  open: boolean;
  setOpen: (e: boolean) => void;
  customerId?: string | null;
  customerMobile?: string;
  customerName?: string;
}

const PLACEHOLDER_IMAGE = "https://vetic.in/_next/image?url=%2Fimages%2Flogo.png&w=128&q=75";

const SmartQCDeeplinkModal: React.FC<ISmartQCDeeplinkModalProps> = ({
  open,
  setOpen,
  customerId,
  customerMobile,
  customerName,
}) => {
  const { execute: fetchCards, cards, isLoading: isCardsLoading } = useSmartQCDeeplinkCardsAPI();
  const { execute: fetchScript } = useSmartQCDeeplinkScriptAPI();
  const { execute: sendLink } = useSendSmartQCDeeplinkWhatsappAPI();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [waTile, setWaTile] = useState<ISmartQCDeeplinkCard | null>(null);
  const [waScript, setWaScript] = useState<string>("");
  const [waMobile, setWaMobile] = useState<string>("");
  const [isScriptLoading, setIsScriptLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      fetchCards();
    } else {
      setSelectedId(null);
      setWaTile(null);
      setWaScript("");
      setIsScriptLoading(false);
      setIsSending(false);
    }
  }, [open, fetchCards]);

  const selectedTile = useMemo(
    () => cards.find((t) => t.card_id === selectedId) ?? null,
    [cards, selectedId],
  );

  const handleOpenWhatsApp = async () => {
    if (!selectedTile) return;
    setWaTile(selectedTile);
    setWaScript("");
    setWaMobile(customerMobile ?? "");
    setIsScriptLoading(true);
    try {
      const { status, data } = await fetchScript({
        card_id: selectedTile.card_id,
        user_id: customerId ?? undefined,
      });
      if (status && data?.whatsapp_text) {
        setWaScript(data.whatsapp_text);
        if (data.phone_number) setWaMobile(data.phone_number);
      } else {
        const greeting = customerName ? `Hi ${customerName},` : "Hi,";
        setWaScript(
          `${greeting}\n\nHere's the link to ${selectedTile.name} on Vetic:\n${selectedTile.url}\n\nThanks,\nTeam Vetic`,
        );
      }
    } finally {
      setIsScriptLoading(false);
    }
  };

  const handleSendWhatsApp = async (mobile: string) => {
    if (!waTile) return;
    const mobileNumber = (mobile ?? "").replace(/\D/g, "");
    if (!mobileNumber) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    setIsSending(true);
    const { status } = await sendLink({
      card_id: waTile.card_id,
      mobile: mobileNumber,
      user_id: customerId ?? undefined,
    });
    setIsSending(false);
    if (status) {
      setWaTile(null);
      setSelectedId(null);
      setOpen(false);
    }
  };

  if (!open) return null;

  const renderCard = (tile: ISmartQCDeeplinkCard) => {
    const isSelected = selectedId === tile.card_id;
    return (
      <button
        key={tile.card_id}
        type="button"
        onClick={() => setSelectedId(tile.card_id)}
        className={`flex flex-col items-center gap-2 p-3 rounded-[12px] text-left transition-all cursor-pointer ${isSelected
          ? "border-2 border-[#7A5AF8] bg-[#F4F0FF]"
          : "border border-[#E5E7EB] bg-[#FAFAFA]"
          }`}
      >
        <div className="flex justify-center items-center bg-white border border-[#EEE] rounded-[12px] w-16 h-16 overflow-hidden">
          <img
            src={tile.image}
            alt={tile.name}
            onError={(e) => {
              (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
            }}
            className="w-12 h-12 object-contain"
          />
        </div>
        <p
          title={tile.name}
          className="m-0 w-full font-semibold text-[#111111] text-xs text-center truncate"
        >
          {tile.name}
        </p>
      </button>
    );
  };

  const actionsDisabled = !selectedTile;

  const modalContent = (
    <div className="z-[100] fixed inset-0 flex justify-center items-center p-4">
      <div
        className="z-[1] absolute inset-0 bg-black/50"
        onClick={() => setOpen(false)}
      />

      <div
        className="z-[2] relative flex flex-col bg-white shadow-xl rounded-[12px] w-full max-w-[680px] max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-5 border-[#EEE] border-b">
          <div>
            <h3 className="m-0 font-semibold text-black text-base">Smart QC Deeplink</h3>
            <p className="mt-1 mb-0 text-[#666] text-xs">
              Select a link{customerMobile ? ` to share with ${customerMobile}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex justify-center items-center bg-[#808DA01A] p-0 border-none rounded-full w-7 h-7 cursor-pointer"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 px-6 py-5 overflow-y-auto">
          {isCardsLoading && cards.length === 0 ? (
            <p className="m-0 text-[#666] text-xs text-center">Loading links...</p>
          ) : cards.length === 0 ? (
            <p className="m-0 text-[#666] text-xs text-center">No links available</p>
          ) : (
            <div className="gap-3 grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))]">
              {cards.map(renderCard)}
            </div>
          )}
        </div>

        <div className="flex gap-3 bg-[#FAFAFA] px-6 py-4 border-[#EEE] border-t">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex flex-1 justify-center items-center gap-1.5 bg-white hover:bg-[#F4F4F4] border border-[#D9DEE7] rounded-[8px] h-10 font-semibold text-[#111111] text-[13px] cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleOpenWhatsApp}
            disabled={actionsDisabled || isScriptLoading}
            className="flex flex-1 justify-center items-center gap-1.5 bg-[#25D366] disabled:bg-[#CDEBD8] border-none rounded-[8px] h-10 font-semibold text-white text-[13px] disabled:cursor-not-allowed cursor-pointer"
          >
            <FaWhatsapp className="w-4 h-4" />
            {isScriptLoading ? "Loading..." : "Send WhatsApp"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!waTile && createPortal(modalContent, document.body)}
      {waTile && (
        <WhatsappTemplatePreviewModal
          open={!!waTile}
          onClose={() => {
            if (!isSending) setWaTile(null);
          }}
          title={`Send ${waTile.name} Link`}
          initialMobile={waMobile}
          script={waScript}
          isSending={isSending}
          onSend={handleSendWhatsApp}
          onCancel={() => setWaTile(null)}
        />
      )}
    </>
  );
};

export default SmartQCDeeplinkModal;
